import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, CircularProgress } from '@mui/material';
import { MaximizeIcon, ArrowRightIcon } from '../../Icons/DashboardIcons';
import TypeOfIssuesChart from './charts/TypeOfIssuesChart';
import IssueResolutionStatusChart from './charts/IssueResolutionStatusChart';
import ReportedIssuesByCategoryChart from './charts/ReportedIssuesByCategoryChart';

// Import reusable components
import { FullscreenChartModal, ErrorDisplay, SampleDataBadge } from './common';

// Import API
import TLDashboardAPIs from '../../utilities/api/dashboard/TLDashboardAPIs';

// Import sample data utilities
import { isDataEmpty, getSampleDataForPeriod } from '../../utilities/sampleData/tlDashboardSampleData';

// Helper function to get priority styling
const getPriorityStyles = (priority) => {
    const priorityLower = (priority || '').toLowerCase();
    switch (priorityLower) {
        case 'high':
            return {
                dotColor: '#c62828',
                bgColor: '#ffebee',
                textColor: '#c62828',
                label: 'High'
            };
        case 'medium':
            return {
                dotColor: '#ff9800',
                bgColor: '#fff3e0',
                textColor: '#f57c00',
                label: 'Medium'
            };
        case 'low':
            return {
                dotColor: '#4caf50',
                bgColor: '#e8f5e9',
                textColor: '#2e7d32',
                label: 'Low'
            };
        default:
            return {
                dotColor: '#9e9e9e',
                bgColor: '#f5f5f5',
                textColor: '#666666',
                label: priority || 'N/A'
            };
    }
};

// Helper function to get status styling
const getStatusStyles = (status) => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
        case 'resolved':
            return {
                dotColor: '#2e7d32',
                textColor: '#2e7d32',
                label: 'Resolved'
            };
        case 'in_progress':
        case 'in progress':
            return {
                dotColor: '#fbc02d',
                textColor: '#fbc02d',
                label: 'In Progress'
            };
        case 'pending':
            return {
                dotColor: '#ea4335',
                textColor: '#ea4335',
                label: 'Pending'
            };
        default:
            return {
                dotColor: '#9e9e9e',
                textColor: '#666666',
                label: status || 'N/A'
            };
    }
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).replace(/\//g, '-');
};

// Helper function to group issues by date
const groupIssuesByDate = (issues) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups = {
        today: [],
        yesterday: [],
        older: []
    };

    issues.forEach(issue => {
        const issueDate = new Date(issue.created_at || issue.date);
        issueDate.setHours(0, 0, 0, 0);

        if (issueDate.getTime() === today.getTime()) {
            groups.today.push(issue);
        } else if (issueDate.getTime() === yesterday.getTime()) {
            groups.yesterday.push(issue);
        } else {
            groups.older.push(issue);
        }
    });

    return groups;
};

const MISDataTab = ({ selectedPeriod = 'weekly' }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [maximizedChart, setMaximizedChart] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Fetch data when period or refreshTrigger changes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await TLDashboardAPIs.getMISData(selectedPeriod);
                setData(response);
            } catch (err) {
                console.error('Error fetching MIS data:', err);
                setError('Failed to load MIS data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedPeriod, refreshTrigger]);

    const handleRetry = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size={48} sx={{ color: '#007bff' }} />
                <Typography sx={{ color: '#666', fontSize: '14px', fontWeight: 500 }}>
                    Fetching MIS data...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return <ErrorDisplay message={error} onRetry={handleRetry} />;
    }

    // Extract data from API response with sample data fallback
    const rawTypesOfIssues = data?.types_of_issues || [];
    const isTypesOfIssuesEmpty = isDataEmpty(rawTypesOfIssues, 'array');
    const typesOfIssues = isTypesOfIssuesEmpty ? getSampleDataForPeriod('typesOfIssues') : rawTypesOfIssues;

    const rawIssueResolution = data?.issue_resolution_status || {
        total_issues: 0,
        resolved: 0,
        in_progress: 0,
        pending: 0
    };
    const isIssueResolutionEmpty = rawIssueResolution.total_issues === 0;
    const issueResolutionStatus = isIssueResolutionEmpty ? getSampleDataForPeriod('issueResolutionStatus') : rawIssueResolution;

    const rawReportedByCategory = data?.reported_issues_by_category || [];
    const isReportedByCategoryEmpty = isDataEmpty(rawReportedByCategory, 'array');
    const reportedIssuesByCategory = isReportedByCategoryEmpty ? getSampleDataForPeriod('reportedIssuesByCategory') : rawReportedByCategory;

    const rawRecentIssues = data?.recent_issues || [];
    const isRecentIssuesEmpty = rawRecentIssues.length === 0;
    const recentIssues = isRecentIssuesEmpty ? getSampleDataForPeriod('recentIssues') : rawRecentIssues;

    // Group recent issues by date
    const groupedIssues = groupIssuesByDate(recentIssues);

    // Helper function to get chart title
    const getChartTitle = (chartType) => {
        switch (chartType) {
            case 'typeOfIssues': return 'Type of Issues';
            case 'issueResolution': return 'Issue Resolution Status';
            case 'reportedByCategory': return 'Reported Issues by Category';
            case 'recentIssues': return 'Recent Issues';
            default: return '';
        }
    };

    // Helper function to render chart content in fullscreen
    const renderChartContent = (chartType) => {
        switch (chartType) {
            case 'typeOfIssues':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <TypeOfIssuesChart data={typesOfIssues} />
                        </Box>
                    </Box>
                );
            case 'issueResolution':
                return (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IssueResolutionStatusChart data={issueResolutionStatus} />
                    </Box>
                );
            case 'reportedByCategory':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <ReportedIssuesByCategoryChart data={reportedIssuesByCategory} />
                        </Box>
                    </Box>
                );
            case 'recentIssues':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                                height: '100%',
                                overflowY: 'auto'
                            }}
                        >
                            {recentIssues.length > 0 ? (
                                <>
                                    {groupedIssues.today.length > 0 && (
                                        <>
                                            <DateSeparator label="Today" />
                                            {groupedIssues.today.map((issue, index) => (
                                                <IssueItem key={`today-${index}`} issue={issue} />
                                            ))}
                                        </>
                                    )}
                                    {groupedIssues.yesterday.length > 0 && (
                                        <>
                                            <DateSeparator label="Yesterday" />
                                            {groupedIssues.yesterday.map((issue, index) => (
                                                <IssueItem key={`yesterday-${index}`} issue={issue} />
                                            ))}
                                        </>
                                    )}
                                    {groupedIssues.older.length > 0 && (
                                        <>
                                            <DateSeparator label="Earlier" />
                                            {groupedIssues.older.map((issue, index) => (
                                                <IssueItem key={`older-${index}`} issue={issue} />
                                            ))}
                                        </>
                                    )}
                                </>
                            ) : (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%'
                                }}>
                                    <Typography sx={{ color: '#999', fontSize: '14px' }}>
                                        No recent issues
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                );
            default:
                return null;
        }
    };

    // Issue Item Component
    const IssueItem = ({ issue }) => {
        const priorityStyles = getPriorityStyles(issue.priority);
        const statusStyles = getStatusStyles(issue.status);

        return (
            <Box
                sx={{
                    px: 0,
                    border: '1px solid #e6e6e6',
                    borderRadius: '8px',
                    bgcolor: '#ffffff',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                }}
            >
                {/* Top Line */}
                <Box
                    sx={{
                        display: 'flex',
                        py: "15px",
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: "12px"
                    }}
                >
                    {/* Left: Icon + Title */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                        <Box
                            sx={{
                                width: '12px',
                                height: '12px',
                                bgcolor: '#007bff',
                                borderRadius: '2px',
                                flexShrink: 0,
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontFamily: '"Roboto-Regular", "Roboto", sans-serif',
                                color: '#333333',
                                fontWeight: 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {issue.title || issue.description || 'Issue'}
                        </Typography>
                    </Box>
                    {/* Right: Priority Chip + Arrow */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <Chip
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Box
                                        sx={{
                                            width: '6px',
                                            height: '6px',
                                            borderRadius: '50%',
                                            bgcolor: priorityStyles.dotColor,
                                        }}
                                    />
                                    <span>{priorityStyles.label}</span>
                                </Box>
                            }
                            size="small"
                            sx={{
                                bgcolor: priorityStyles.bgColor,
                                color: priorityStyles.textColor,
                                fontSize: '12px',
                                height: '24px',
                                fontWeight: 500,
                                '& .MuiChip-label': {
                                    padding: '0 8px',
                                },
                            }}
                        />
                        <ArrowRightIcon color="#000000" />
                    </Box>
                </Box>
                {/* Bottom Line */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: "12px",
                        py: "14px",
                        backgroundColor: '#f5f5f5',
                    }}
                >
                    {/* Left: Date */}
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontFamily: '"Roboto-Regular", "Roboto", sans-serif',
                            color: '#666666',
                        }}
                    >
                        {formatDate(issue.created_at || issue.date)}
                    </Typography>
                    {/* Right: Status with dot */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Box
                            sx={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                bgcolor: statusStyles.dotColor,
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontFamily: '"Roboto-Regular", "Roboto", sans-serif',
                                color: statusStyles.textColor,
                                fontWeight: 600,
                            }}
                        >
                            {statusStyles.label}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    // Date Separator Component
    const DateSeparator = ({ label }) => (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                my: '8px',
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    height: '1px',
                    bgcolor: '#e6e6e6',
                }}
            />
            <Typography
                sx={{
                    px: '12px',
                    fontSize: '12px',
                    fontFamily: '"Roboto-Regular", "Roboto", sans-serif',
                    color: '#666666',
                    bgcolor: '#ffffff',
                }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    flex: 1,
                    height: '1px',
                    bgcolor: '#e6e6e6',
                }}
            />
        </Box>
    );

    return (
        <Box>
            {/* Fullscreen Chart Modal */}
            <FullscreenChartModal
                open={Boolean(maximizedChart)}
                onClose={() => setMaximizedChart(null)}
                title={getChartTitle(maximizedChart)}
            >
                {renderChartContent(maximizedChart)}
            </FullscreenChartModal>
            {/* First Row */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: { xs: '16px', md: '24px' },
                }}
            >
                {/* Type of Issues */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: { xs: '8px', md: '24px' },
                        border: '1px solid #e6e6e6',
                        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
                        overflow: 'hidden',
                        height: { xs: '320px', sm: '360px', md: '408px' },
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            padding: { xs: '10px', md: '12px' },
                            borderBottom: '1px solid #e6e6e6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: '14px', md: '16px' },
                                    fontWeight: 500,
                                    fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                                    color: '#333333',
                                }}
                            >
                                Type of Issues
                            </Typography>
                            <SampleDataBadge show={isTypesOfIssuesEmpty} absolute={false} />
                        </Box>
                        <Box
                            onClick={() => setMaximizedChart('typeOfIssues')}
                            sx={{
                                padding: { xs: '6px', md: '8px' },
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: '#f5f5f5',
                                    borderColor: '#c144ff',
                                },
                            }}
                        >
                            <MaximizeIcon />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            padding: { xs: '12px', sm: '16px', md: '20px' },
                            flex: 1,
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: '#fafafa',
                        }}
                    >
                        <TypeOfIssuesChart data={typesOfIssues} />
                    </Box>
                </Box>

                {/* Issue Resolution Status */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: { xs: '8px', md: '24px' },
                        border: '1px solid #e6e6e6',
                        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
                        overflow: 'hidden',
                        height: { xs: '320px', sm: '360px', md: '408px' },
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            padding: { xs: '10px', md: '12px' },
                            borderBottom: '1px solid #e6e6e6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: '14px', md: '16px' },
                                    fontWeight: 500,
                                    fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                                    color: '#333333',
                                }}
                            >
                                Issue Resolution Status
                            </Typography>
                            <SampleDataBadge show={isIssueResolutionEmpty} absolute={false} />
                        </Box>
                        <Box
                            onClick={() => setMaximizedChart('issueResolution')}
                            sx={{
                                padding: { xs: '6px', md: '8px' },
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: '#f5f5f5',
                                    borderColor: '#12ab0a',
                                },
                            }}
                        >
                            <MaximizeIcon />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            padding: { xs: '12px', sm: '16px', md: '20px' },
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: '#fafafa',
                        }}
                    >
                        <IssueResolutionStatusChart data={issueResolutionStatus} />
                    </Box>
                </Box>
            </Box>

            {/* Second Row */}
            <Box
                sx={{
                    mt: { xs: 2, md: 3 },
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: { xs: '16px', md: '24px' },
                }}
            >
                {/* Reported Issues by Category */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: { xs: '8px', md: '24px' },
                        border: '1px solid #e6e6e6',
                        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
                        overflow: 'hidden',
                        height: { xs: '300px', sm: '350px', md: '408px' },
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            padding: { xs: '10px', md: '12px' },
                            borderBottom: '1px solid #e6e6e6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: '14px', md: '16px' },
                                    fontWeight: 500,
                                    fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                                    color: '#333333',
                                }}
                            >
                                Reported Issues by Category
                            </Typography>
                            <SampleDataBadge show={isReportedByCategoryEmpty} absolute={false} />
                        </Box>
                        <Box
                            onClick={() => setMaximizedChart('reportedByCategory')}
                            sx={{
                                padding: { xs: '6px', md: '8px' },
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: '#f5f5f5',
                                    borderColor: '#f99f24',
                                },
                            }}
                        >
                            <MaximizeIcon />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            padding: { xs: '12px', sm: '16px', md: '20px' },
                            flex: 1,
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: '#fafafa',
                        }}
                    >
                        <ReportedIssuesByCategoryChart data={reportedIssuesByCategory} />
                    </Box>
                </Box>

                {/* Recent Issues */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: { xs: '8px', md: '24px' },
                        border: '1px solid #e6e6e6',
                        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
                        overflow: 'hidden',
                        height: { xs: '300px', sm: '350px', md: '408px' },
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            padding: { xs: '10px', md: '12px' },
                            borderBottom: '1px solid #e6e6e6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: '14px', md: '16px' },
                                    fontWeight: 500,
                                    fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                                    color: '#333333',
                                }}
                            >
                                Recent Issues
                            </Typography>
                            <SampleDataBadge show={isRecentIssuesEmpty} absolute={false} />
                        </Box>
                        <Box
                            onClick={() => setMaximizedChart('recentIssues')}
                            sx={{
                                padding: { xs: '6px', md: '8px' },
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: '#f5f5f5',
                                    borderColor: '#007bff',
                                },
                            }}
                        >
                            <MaximizeIcon />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            padding: { xs: '12px', sm: '16px', md: '20px' },
                            flex: 1,
                            overflowY: 'auto',
                            bgcolor: '#fafafa',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                                bgcolor: '#ffffff',
                                border: '1px solid #e6e6e6',
                                borderRadius: '20px',
                                padding: '20px',
                                height: '100%',
                                overflowY: 'auto'
                            }}
                        >
                            {recentIssues.length > 0 ? (
                                <>
                                    {/* Today's Issues */}
                                    {groupedIssues.today.length > 0 && (
                                        <>
                                            <DateSeparator label="Today" />
                                            {groupedIssues.today.map((issue, index) => (
                                                <IssueItem key={`today-${index}`} issue={issue} />
                                            ))}
                                        </>
                                    )}

                                    {/* Yesterday's Issues */}
                                    {groupedIssues.yesterday.length > 0 && (
                                        <>
                                            <DateSeparator label="Yesterday" />
                                            {groupedIssues.yesterday.map((issue, index) => (
                                                <IssueItem key={`yesterday-${index}`} issue={issue} />
                                            ))}
                                        </>
                                    )}

                                    {/* Older Issues */}
                                    {groupedIssues.older.length > 0 && (
                                        <>
                                            <DateSeparator label="Earlier" />
                                            {groupedIssues.older.map((issue, index) => (
                                                <IssueItem key={`older-${index}`} issue={issue} />
                                            ))}
                                        </>
                                    )}
                                </>
                            ) : (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%'
                                }}>
                                    <Typography sx={{ color: '#999', fontSize: '14px' }}>
                                        No recent issues
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MISDataTab;
